import React, { useState } from "react";
import { Icon } from "@iconify/react";
import StatusBadge from "./StatusBadge";
import { getLaunchId } from "./automationConfigs";
import { describeCronExpression } from "./cronExpression";

const formatDate = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
};

const notificationTypeIcon = (type) =>
  /EMAIL/i.test(type) ? "mdi:email-outline" : "mdi:bell-badge-outline";

// Big popup with the template rendered at full size. Opened by the eye
// button on the thumbnail; closes on backdrop click or the close button.
const TemplatePreviewModal = ({ template, hasInApp, onClose }) => {
  if (!template) return null;

  return (
    <div
      className="template-preview-modal__backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="template-preview-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="template-preview-modal__header">
          <div>
            <h3>{template.templateName || "Template preview"}</h3>
            {template.subject && (
              <p className="template-preview-modal__subject">
                <strong>Subject:</strong> {template.subject}
              </p>
            )}
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close preview"
          >
            <Icon icon="mdi:close" width="20" />
          </button>
        </header>

        <div className="template-preview-modal__body">
          {template.htmlBody ? (
            <iframe
              title={`${template.templateName || "template"}-full-preview`}
              className="template-preview-modal__iframe"
              srcDoc={template.body}
              sandbox=""
            />
          ) : (
            <pre className="cron-job-card__plain-body">{template.body}</pre>
          )}
        </div>

        {hasInApp &&
          (template.notificationHeading || template.notificationMessage) && (
            <div className="cron-job-card__inapp-preview">
              <span className="cron-job-card__inapp-label">
                <Icon icon="mdi:bell-badge-outline" width="14" />
                In-app notification
              </span>
              {template.notificationHeading && (
                <p className="cron-job-card__inapp-heading">
                  {template.notificationHeading}
                </p>
              )}
              {template.notificationMessage && (
                <p className="cron-job-card__inapp-message">
                  {template.notificationMessage}
                </p>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

// Small always-on thumbnail: the template rendered at a fixed larger size
// then scaled down with CSS so it reads as a genuine miniature of the real
// email rather than a placeholder. An eye button in the corner opens the
// full-size popup — no click needed just to see *something*.
const TemplateThumbnail = ({ template, onOpenPreview }) => {
  if (!template) return null;

  return (
    <div className="cron-job-card__template">
      {template.subject && (
        <p className="cron-job-card__subject">
          <strong>Subject:</strong> {template.subject}
        </p>
      )}

      <div className="template-thumb">
        {template.htmlBody ? (
          <iframe
            title={`${template.templateName || "template"}-thumb`}
            className="template-thumb__iframe"
            srcDoc={template.body}
            sandbox=""
            tabIndex={-1}
          />
        ) : (
          <div className="template-thumb__plain">{template.body}</div>
        )}
        <button
          type="button"
          className="template-thumb__eye"
          onClick={onOpenPreview}
          aria-label="Open full template preview"
        >
          <Icon icon="mdi:eye-outline" width="16" />
        </button>
      </div>
    </div>
  );
};

const ScheduledItemCard = ({
  config,
  item,
  selected,
  onToggleSelect,
  onEdit,
  onDelete,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const launchId = getLaunchId(config, item);
  const launchInfo = item[config.launchInfoField] || {};
  const template = item[config.templateField];
  const notificationTypes = item.notificationTypes || [];
  const hasInApp = notificationTypes.some((nt) =>
    /IN_APP/i.test(nt.notificationType),
  );

  return (
    <div className="cron-job-card">
      <header className="cron-job-card__header">
        <input
          type="checkbox"
          checked={launchId != null && selected}
          disabled={launchId == null}
          onChange={() => onToggleSelect(launchId)}
          aria-label={`Select ${item.taskName}`}
          title={launchId == null ? "No record to launch yet" : undefined}
        />
        <h4 className="cron-job-card__title">{item.taskName}</h4>
        <button
          type="button"
          className="icon-button"
          onClick={() => onEdit(item)}
          aria-label={`Edit ${item.taskName}`}
        >
          <Icon icon="mdi:pencil-outline" width="18" />
        </button>
        <button
          type="button"
          className="icon-button icon-button--danger"
          onClick={() => onDelete(item)}
          disabled={launchId == null}
          aria-label={`Delete ${item.taskName}`}
          title={
            launchId == null
              ? "Not launched yet — nothing to delete"
              : undefined
          }
        >
          <Icon icon="mdi:trash-can-outline" width="18" />
        </button>
      </header>

      <div className="cron-job-card__badges">
        <StatusBadge value={item.status} />
        <StatusBadge value={launchInfo.status} />
        {notificationTypes.map((nt) => (
          <span key={nt.notificationTypeId} className="cron-job-card__chip">
            <Icon icon={notificationTypeIcon(nt.notificationType)} width="14" />
            {nt.notificationType}
          </span>
        ))}
      </div>

      <dl className="cron-job-card__meta">
        {config.hasCronFields && (
          <div>
            <dt>Schedule</dt>
            <dd>
              {item.cronJobExpression
                ? describeCronExpression(item.cronJobExpression)
                : `${item.triggerDate || ""} ${item.days ?? ""}`.trim() || "—"}
            </dd>
          </div>
        )}
        <div>
          <dt>Send to</dt>
          <dd>{item.toEmailSourceFrom || "—"}</dd>
        </div>
        <div>
          <dt>Send from</dt>
          <dd>
            {/SPECIFIC/i.test(item.fromEmailSource || "")
              ? item.fromEmail
              : item.fromEmailSource || "—"}
          </dd>
        </div>
        <div>
          <dt>Last launched</dt>
          <dd>{formatDate(launchInfo.recentLaunchDate)}</dd>
        </div>
      </dl>

      <TemplateThumbnail
        template={template}
        onOpenPreview={() => setPreviewOpen(true)}
      />

      {previewOpen && (
        <TemplatePreviewModal
          template={template}
          hasInApp={hasInApp}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
};

// Drop-in replacement for AutomationTable — same props — used as the
// ItemsView for both the Notification (events) and Automation (cron jobs)
// tabs. Groups the flattened items by their type label (event type / cron
// job type) and renders each as a card instead of a table row.
const ScheduledAutomationCards = ({
  config,
  items,
  loading,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onEdit,
  onDelete,
}) => {
  if (loading) {
    return (
      <div className="automation-table__state">
        Loading {config.labelPlural.toLowerCase()}…
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="automation-table__state">
        <Icon icon="mdi:calendar-blank-outline" width="28" />
        <p>
          No {config.labelPlural.toLowerCase()} yet. Use “New” to create one.
        </p>
      </div>
    );
  }

  const groups = items.reduce((acc, item) => {
    const label = item[config.typeLabelField] || "Other";
    (acc[label] = acc[label] || []).push(item);
    return acc;
  }, {});

  const selectableItems = items.filter(
    (item) => getLaunchId(config, item) != null,
  );
  const allSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedIds.has(getLaunchId(config, item)));

  return (
    <div className="cron-job-cards">
      <label className="cron-job-cards__select-all">
        <input
          type="checkbox"
          checked={allSelected}
          onChange={(e) => onToggleSelectAll(e.target.checked)}
        />
        Select all
      </label>

      {Object.entries(groups).map(([typeLabel, groupItems]) => (
        <section key={typeLabel} className="cron-job-cards__group">
          <h4 className="cron-job-cards__group-title">
            {typeLabel}
            <span className="cron-job-cards__group-count">
              {groupItems.length}
            </span>
          </h4>
          <div className="cron-job-cards__grid">
            {groupItems.map((item) => (
              <ScheduledItemCard
                key={item[config.idField]}
                config={config}
                item={item}
                selected={selectedIds.has(getLaunchId(config, item))}
                onToggleSelect={onToggleSelect}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default ScheduledAutomationCards;
